import * as React from "react";
import { Link } from "react-router-dom";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../../ui/tabs";

export type TabsItem = {
    id: string;
    name: string;
    mobileName?: string;
    content?: React.ReactNode;
    href?: string;
    linkState?: unknown;
    icon?: React.ReactNode;
    triggerClassName?: string;
};

export type TabsMap = Record<string, TabsItem>;

export type HHTabsProps = {
    activeTab: string;
    setActiveTab: (id: string) => void;
    tabs: TabsMap;
    className?: string;
    listClassName?: string;
    triggerClassName?: string;
    equalWidth?: boolean;
};

export const HHTabs: React.FC<HHTabsProps> = ({
    activeTab,
    setActiveTab,
    tabs,
    className,
    listClassName,
    triggerClassName,
    equalWidth = false,
}) => {
    const items = Object.values(tabs);
    const defaultValue = items[0]?.id ?? "";

    return (
        <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            defaultValue={defaultValue}
            className={[className ?? ""].join(" ")}
        >
            <TabsList
                className={[
                    "!h-auto flex max-w-full flex-wrap items-stretch gap-1 rounded-[18px] border border-[#e4e7ec] bg-white p-1 shadow-[0_8px_24px_rgba(15,23,42,0.06)]",
                    equalWidth ? "w-full" : "w-fit",
                    listClassName ?? "",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {items.map((tab) => {
                    const tabClasses = [
                        "!h-auto min-h-[44px] self-stretch rounded-[14px] px-4 py-3 text-[14px] font-semibold leading-none tracking-[0.01em] text-[#667085] transition-all data-[state=active]:border-[#e4e7ec] data-[state=active]:bg-[#f8fafc] data-[state=active]:text-[#101828] data-[state=active]:shadow-none sm:min-h-[46px]",
                        equalWidth ? "flex-1" : "",
                        triggerClassName ?? "",
                        tab.triggerClassName ?? "",
                    ]
                        .filter(Boolean)
                        .join(" ");
                    const tabLabel = (
                        <>
                            {tab.icon ? <span aria-hidden="true">{tab.icon}</span> : null}
                            {tab.mobileName ? (
                                <>
                                    <span className="sm:hidden">{tab.mobileName}</span>
                                    <span className="hidden sm:inline">{tab.name}</span>
                                </>
                            ) : (
                                <span>{tab.name}</span>
                            )}
                        </>
                    );

                    return tab.href ? (
                        <TabsTrigger key={tab.id} value={tab.id} className={tabClasses} asChild>
                            <Link
                                to={tab.href}
                                state={tab.linkState}
                                aria-label={tab.name}
                                aria-current={activeTab === tab.id ? "page" : undefined}
                            >
                                {tabLabel}
                            </Link>
                        </TabsTrigger>
                    ) : (
                        <TabsTrigger
                            key={tab.id}
                            value={tab.id}
                            className={tabClasses}
                            aria-label={tab.mobileName ? tab.name : undefined}
                        >
                            {tabLabel}
                        </TabsTrigger>
                    );
                })}
            </TabsList>

            {items.map(
                (tab) =>
                    tab.content && (
                        <TabsContent key={tab.id} value={tab.id}>
                            {tab.content}
                        </TabsContent>
                    )
            )}
        </Tabs>
    );
};
